/**
 * @file permissions.js
 * @description Utilitários de permissões (CRA)
 */

import { isPermissionFlagEnabled } from "../../../../lib/is-permission-flag-enabled.js"

/**
 * Resolve role por nome; se houver duplicatas, usa a última (mais recente no array).
 * @param {Array} permissions
 * @param {string} permissionName
 * @returns {Object|null}
 */
export function findPermissionRoleByName(permissions, permissionName) {
  if (!permissions || permissions.length === 0) {
    return null
  }

  const matches = permissions.filter((item) => item.name === permissionName)
  if (matches.length === 0) {
    return null
  }

  return matches[matches.length - 1]
}

/**
 * Verifica se o usuário é administrador
 * @param {Array} permissions - Array de permissões do usuário
 * @returns {boolean} True se o usuário é admin
 */
export function isAdmin(permissions) {
  if (!permissions || permissions.length === 0) {
    return false
  }

  const adminRoles = ["admin", "administrador", "administrator"]
  return permissions.some(
    (role) => adminRoles.includes(role.name?.toLowerCase() || "")
  )
}

/**
 * Verifica ação específica de uma permissão (boolean, 1 ou "true"/"1").
 * Alinhado com ProtectedRoute e permissionsContext.
 * @param {Array} permissions - Array de permissões do usuário
 * @param {string} permissionName - Nome da permissão
 * @param {'islist' | 'isinsert' | 'isremove'} [action='islist'] - Ação específica
 * @returns {boolean}
 */
export function hasPermissionAction(permissions, permissionName, action = "islist") {
  if (!permissions || permissions.length === 0) return false

  const matches = permissions.filter((item) => item.name === permissionName)

  return (
    matches.length > 0 &&
    matches.every((item) => isPermissionFlagEnabled(item[action]))
  )
}

/**
 * Verifica se o usuário tem uma permissão (com ou sem ação).
 * @param {Array} permissions - Array de permissões do usuário
 * @param {string} permissionName - Nome da permissão
 * @param {'islist' | 'isinsert' | 'isremove'} [action] - Ação específica
 * @returns {boolean} True se o usuário tem a permissão
 */
export function hasPermission(permissions, permissionName, action) {
  if (!permissions || permissions.length === 0) return false

  const role = findPermissionRoleByName(permissions, permissionName)
  if (!role) {
    return false
  }

  if (!action) {
    return true
  }

  return hasPermissionAction(permissions, permissionName, action)
}

/**
 * Obtém as permissões do localStorage
 * @returns {Array} Array de permissões
 */
export function getPermissionsFromStorage() {
  try {
    const storagedUser = localStorage.getItem("user_token")
    if (storagedUser) {
      const user = JSON.parse(storagedUser)
      return user.roles || []
    }
  } catch (error) {
    console.error("Erro ao carregar permissões do localStorage:", error)
  }
  return []
}
